import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SessionActions } from '@state/session';
import { IndexedDBService } from '@service/indexedDB/indexed-db.service';

@Injectable({
	providedIn: 'root',
})
export class SessionService {
	constructor(
		private store: Store,
		private router: Router,
		private indexedDB: IndexedDBService
	) {}

	/**
	 * Navigate first, tear down only once we know we are actually leaving.
	 *
	 * A canDeactivate guard can veto this navigation — Booking Automation does
	 * exactly that when the page has unsaved changes. Tearing the session down
	 * beforehand meant Cancel left the user on a page that still looked signed
	 * in but had no session behind it: the dialog offers to go back and save,
	 * and the save could not succeed. Clearing the store also re-emitted the
	 * page's data as empty, repainting the form with values the user never
	 * chose. navigate() resolves false when a guard cancels.
	 */
	endSession(): void {
		// Rejection is handled as the second argument rather than a trailing
		// catch, so it only ever sees a navigation failure — a throw from the
		// teardown below would otherwise be reported as one.
		this.router.navigate(['/login']).then(
			(navigated) => {
				if (navigated) {
					this.tearDownSession();
				}
			},
			(error) => {
				// The user is still on the page, so the session has to stay
				// usable. Surfaced rather than swallowed, and not torn down.
				console.error('Logout navigation failed', error);
			}
		);
	}

	/**
	 * localStorage holds the credentials, so clearing it is what actually ends
	 * the session — it runs in a finally so an earlier step failing cannot
	 * leave the user half logged out.
	 */
	private tearDownSession(): void {
		try {
			this.store.dispatch(SessionActions.logout.start());
			this.indexedDB.updateShowDashboardSetting(true);
		} catch (error) {
			console.error('Logout teardown failed', error);
		} finally {
			localStorage.clear();
		}
	}
}
